import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { SetupModal } from './SetupModal'
import { isSetupAllowedForUser } from 'utils/setupStorage'
import { RootState } from 'constants/interfaces'
import { useSetup } from 'contexts/SetupContext'
import keys from 'constants/keys'

/**
 * SetupModalWrapper Component
 * Listens for custom events and opens the corresponding setup modal
 * Acts as a bridge between the step navigation and modal system
 */
export const SetupModalWrapper: React.FC = () => {
  const uid = useSelector((state: RootState) => state.auth.uid)
  const location = useLocation()
  const { setModalOpen } = useSetup()
  const [modalState, setModalState] = useState({
    isOpen: false,
    stepId: 0,
    stepTitle: '',
  })

  /**
   * Listen for custom 'openSetupModal' events
   * Triggered when user clicks "Get Started" on a step
   */
  useEffect(() => {
    const handleOpenSetupModal = (event: CustomEvent<{ stepId: number; stepTitle: string }>) => {
      // Only open modal if user is allowed
      if (!isSetupAllowedForUser(uid)) return

      if (
        location.pathname === keys.ROUTE_NAMES.SETUP ||
        location.pathname === keys.ROUTE_NAMES.ORGANIZATION_TREE ||
        location.pathname === keys.ROUTE_NAMES.SALES_OPPORTUNITIES
      )
        return

      const { stepId, stepTitle } = event.detail
      setModalState({
        isOpen: true,
        stepId,
        stepTitle,
      })
      setModalOpen(true)
    }

    window.addEventListener('openSetupModal', handleOpenSetupModal as EventListener)

    return () => {
      window.removeEventListener('openSetupModal', handleOpenSetupModal as EventListener)
    }
  }, [uid, setModalOpen, location.pathname])

  const handleClose = () => {
    setModalState({
      isOpen: false,
      stepId: 0,
      stepTitle: '',
    })
    setModalOpen(false)
  }

  return (
    <SetupModal
      stepId={modalState.stepId}
      stepTitle={modalState.stepTitle}
      isOpen={modalState.isOpen}
      onClose={handleClose}
    />
  )
}
